import React, { useState, useMemo } from 'react';
import useRealtimeDraft from '../../realtime/useRealtimeDraft';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Row, Col, Form, Button, Badge, Modal, Table } from 'react-bootstrap';
import moment from 'moment';
import { updateJob } from '../../redux/slices/jobScheduleSlice';
import { patchData as patchDataThunk } from '../../redux/slices/apiCallSlice';
import { selectPtoEntries } from '../../redux/slices/ptoSlice';
import { selectHolidays } from '../../redux/slices/holidaysSlice';
import useWeekCalculations from '../../functions/useWeekCalculations';
import { formatDateUnified } from '../../utils/dateUtils';
import { hasAnyEditPermission } from './utils/schedulePermissions';
import './EngineeringScheduleOptimizer.css';

const EngineeringScheduleOptimizer = ({ filteredJobs = null, canEdit = true }) => {
  // Redux selectors
  const jobScheduleData = useSelector((state) => state.jobSchedule.data);
  const jobSchedule = filteredJobs || jobScheduleData;
  const userRoles = useSelector((state) => state.user?.userRoles) || [];
  const userRolesMapped = useSelector((state) => state.user?.rolesMapped) || {};
  const mechOptions = useSelector((state) => state.dropdown.userDropdowns.mechOptions);
  const elecOptions = useSelector((state) => state.dropdown.userDropdowns.elecOptions);
  const controlOptions = useSelector((state) => state.dropdown.userDropdowns.controlOptions || []);
  const ptoEntries = useSelector(selectPtoEntries);
  const holidays = useSelector(selectHolidays);
  const apiUrls = useSelector((state) => state.apiUrls.urls);
  const dispatch = useDispatch();
  
  // Use week calculations hook with 52 weeks
  const { workWeekColumns } = useWeekCalculations(52);
  
  // Access control using centralized permission system
  // Exclude accounting users from editing in engineering views
  const hasEditAccess = Boolean(canEdit) && hasAnyEditPermission(userRoles) && !userRolesMapped.Accounting;
  
  // Filter states
  const [filters, setFilters] = useState({
    departments: {
      mechanical: true,
      electrical: true,
      controls: true
    },
    engineers: {
      mechanical: [],
      electrical: [],
      controls: []
    },
    startWeek: 1,
    endWeek: 4,
    jobTypes: {
      all: true,
      hc: false,
      wm: false,
      vtv: false,
      sky: false,
      retrofit: false
    },
    showCompleted: false,
    showUnscheduled: false // Default to false
  });
  
  // Modal states
  const [showJobModal, setShowJobModal] = useState(false);
  useRealtimeDraft(showJobModal);
  const [selectedJob, setSelectedJob] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [showCompactFilters, setShowCompactFilters] = useState(false);

  const parseDurationDays = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined || value === '') continue;

      if (typeof value === 'number' && Number.isFinite(value)) {
        if (value > 0) return Math.ceil(value);
        continue;
      }

      const valueAsString = String(value).trim();
      if (!valueAsString) continue;

      const normalized = valueAsString.replace(',', '.');
      const match = normalized.match(/-?\d+(?:\.\d+)?/);
      if (!match) continue;

      const parsed = Number(match[0]);
      if (Number.isFinite(parsed) && parsed > 0) {
        return Math.ceil(parsed);
      }
    }

    return 1;
  };

  const getDurationForField = (job, durationField) => {
    const fallbackByField = {
      MechanicalDuration: ['MechanicalDur', 'Mechanical_Duration'],
      ElectricalDuration: ['ElectricalDur', 'Electrical_Duration'],
      ControlsDuration: ['ControlsDur', 'Controls_Duration'],
    };

    const specificFallbacks = (fallbackByField[durationField] || []).map((key) => job?.[key]);

    return parseDurationDays(
      job?.[durationField],
      ...specificFallbacks,
      job?.Duration,
      job?.EngineeringDuration,
      job?.[`${durationField}Days`],
      job?.[durationField?.toLowerCase()]
    );
  };
  
  // Build engineer lists
  const engineers = useMemo(() => {
    const mechanical = (mechOptions || []).map(u => ({
      name: u.displayName,
      type: 'mechanical',
      field: 'MechanicalEngineer',
      durationField: 'MechanicalDuration',
      doneField: 'MechanicalDone',
      dueDateField: 'MechanicalDue'
    }));
    const electrical = (elecOptions || []).map(u => ({
      name: u.displayName,
      type: 'electrical', 
      field: 'ElectricalEngineer',
      durationField: 'ElectricalDuration',
      doneField: 'ElectricalDone',
      dueDateField: 'ElectricalDue'
    }));
    const controls = (controlOptions || []).map(u => ({
      name: u.displayName,
      type: 'controls',
      field: 'ControlsEngineer', 
      durationField: 'ControlsDuration',
      doneField: 'ControlsDone',
      dueDateField: 'ControlsDue'
    }));
    // For engineer filter dropdown
    const all = [...mechanical, ...electrical, ...controls];
    return { mechanical, electrical, controls, all };
  }, [mechOptions, elecOptions, controlOptions]);
  
  // Get visible weeks based on filter
  const visibleWeeks = useMemo(() => {
    if (!workWeekColumns.length) return [];
    return workWeekColumns.slice(filters.startWeek - 1, filters.endWeek);
  }, [workWeekColumns, filters.startWeek, filters.endWeek]);

  // Get the first visible week start date for past-due logic
  const firstVisibleWeekStart = useMemo(() => {
    return visibleWeeks.length > 0 ? moment(visibleWeeks[0].startDate) : null;
  }, [visibleWeeks]);

  // Get the actual Week 1 start date (never changes)
  const week1StartDate = useMemo(() => {
    return workWeekColumns.length > 0 ? moment(workWeekColumns[0].startDate) : null;
  }, [workWeekColumns]);

  // Filter and organize jobs by engineer and week, including past due
  const organizedJobs = useMemo(() => {
    if (!Array.isArray(jobSchedule)) return {};
    const organized = {};
    // Initialize structure for all filtered engineers
    ['mechanical', 'electrical', 'controls'].forEach(type => {
      if (!filters.departments[type]) return;
      engineers[type].forEach(engineer => {
        // Use discipline-specific engineer filter
        if (filters.engineers[type].length > 0 && !filters.engineers[type].includes(engineer.name)) {
          return;
        }
        organized[engineer.name] = {
          engineer: { ...engineer, type },
          weeks: {},
          unscheduled: [],
          pastDue: [] // Add pastDue array
        };
        visibleWeeks.forEach(week => {
          organized[engineer.name].weeks[week.weekNum] = [];
        });
      });
    });
    jobSchedule.forEach(job => {
      if (!filters.showCompleted && job.ActualShipDate) return;
      if (!filters.jobTypes.all) {
        const modelNum = job.ModelNumber?.toUpperCase() || '';
        const matchesFilter = 
          (filters.jobTypes.hc && modelNum.startsWith('HC')) ||
          (filters.jobTypes.wm && modelNum.startsWith('WM')) ||
          (filters.jobTypes.vtv && modelNum.startsWith('VTV')) ||
          (filters.jobTypes.sky && modelNum.startsWith('SKY')) ||
          (filters.jobTypes.retrofit && modelNum.startsWith('RET'));
        if (!matchesFilter) return;
      }
      ['mechanical', 'electrical', 'controls'].forEach(type => {
        if (!filters.departments[type]) return;
        const engineerField = type === 'mechanical' ? 'MechanicalEngineer' : 
                            type === 'electrical' ? 'ElectricalEngineer' : 'ControlsEngineer';
        const durationField = type === 'mechanical' ? 'MechanicalDuration' : 
                             type === 'electrical' ? 'ElectricalDuration' : 'ControlsDuration';
        const doneField = type === 'mechanical' ? 'MechanicalDone' : 
                         type === 'electrical' ? 'ElectricalDone' : 'ControlsDone';
        const dueDateField = type === 'mechanical' ? 'MechanicalDue' : 
                            type === 'electrical' ? 'ElectricalDue' : 'ControlsDue';
        const assignedEngineer = job[engineerField];
        if (!assignedEngineer || !organized[assignedEngineer]) return;
        const dueDate = job[dueDateField];
        const isDone = job[doneField];
        if (isDone && !filters.showCompleted) return;
        const jobWithEngineeringInfo = {
          ...job,
          engineerType: type,
          duration: getDurationForField(job, durationField),
          dueDate: dueDate,
          isDone: isDone,
          uniqueId: `${job.ID}-${type}`
        };
        if (dueDate) {
          const dueMoment = moment(dueDate);
          let placed = false;
          // Past due: before Week 1 (not before first visible week)
          if (week1StartDate && dueMoment.isBefore(week1StartDate, 'day')) {
            organized[assignedEngineer].pastDue.push(jobWithEngineeringInfo);
            placed = true;
          } else {
            // Check if job falls within visible weeks
            for (const week of visibleWeeks) {
              const weekStart = moment(week.startDate);
              const weekEnd = moment(week.endDate);
              if (dueMoment.isBetween(weekStart, weekEnd, 'day', '[]')) {
                organized[assignedEngineer].weeks[week.weekNum].push(jobWithEngineeringInfo);
                placed = true;
                break;
              }
            }
            // If job is not in visible weeks and not past due, check if it should be filtered out
            if (!placed) {
              // If the job is between week 1 and the start filter week, don't include it
              if (filters.startWeek > 1 && firstVisibleWeekStart &&
                  dueMoment.isBetween(week1StartDate, firstVisibleWeekStart, 'day', '[)')) {
                // Job is in weeks before the filter range but not past due - exclude it
                return;
              }
              
              // Otherwise, if showUnscheduled is true, add to unscheduled
              if (filters.showUnscheduled) {
                organized[assignedEngineer].unscheduled.push(jobWithEngineeringInfo);
              }
            }
          }
        } else if (filters.showUnscheduled) {
          organized[assignedEngineer].unscheduled.push(jobWithEngineeringInfo);
        }
      });
    });
    return organized;
  }, [jobSchedule, engineers, filters, visibleWeeks, firstVisibleWeekStart, week1StartDate, getDurationForField]);
  
  // Handle job card double-click
  const handleJobDoubleClick = (job) => {
    if (!hasEditAccess) return;
    setSelectedJob(job);
    setEditingJob({ ...job });
    setShowJobModal(true);
  };
  
  // Close modal
  const handleCloseModal = () => {
    setShowJobModal(false);
    setSelectedJob(null);
    setEditingJob(null);
  };
  
  // Save job changes
  const handleSaveJob = async () => {
    if (!editingJob) return;
    
    try {
      // Create patch data with only the fields that can be edited
      const patchData = {
        ID: editingJob.ID
      };
      
      // Add only the relevant engineering fields based on the job's engineer type
      if (selectedJob?.engineerType === 'mechanical') {
        if (editingJob.MechanicalEngineer !== selectedJob.MechanicalEngineer) {
          patchData.MechanicalEngineer = editingJob.MechanicalEngineer;
        }
        if (editingJob.MechanicalDue !== selectedJob.MechanicalDue) {
          patchData.MechanicalDue = editingJob.MechanicalDue;
        }
        if (editingJob.MechanicalDuration !== selectedJob.MechanicalDuration) {
          patchData.MechanicalDuration = editingJob.MechanicalDuration;
        }
      } else if (selectedJob?.engineerType === 'electrical') {
        if (editingJob.ElectricalEngineer !== selectedJob.ElectricalEngineer) {
          patchData.ElectricalEngineer = editingJob.ElectricalEngineer;
        }
        if (editingJob.ElectricalDue !== selectedJob.ElectricalDue) {
          patchData.ElectricalDue = editingJob.ElectricalDue;
        }
        if (editingJob.ElectricalDuration !== selectedJob.ElectricalDuration) {
          patchData.ElectricalDuration = editingJob.ElectricalDuration;
        }
      } else if (selectedJob?.engineerType === 'controls') {
        if (editingJob.ControlsEngineer !== selectedJob.ControlsEngineer) {
          patchData.ControlsEngineer = editingJob.ControlsEngineer;
        }
        if (editingJob.ControlsDue !== selectedJob.ControlsDue) {
          patchData.ControlsDue = editingJob.ControlsDue;
        }
        if (editingJob.ControlsDuration !== selectedJob.ControlsDuration) {
          patchData.ControlsDuration = editingJob.ControlsDuration;
        }
      }
      
      // Only make the API call if there are actual changes
      if (Object.keys(patchData).length > 1) { // More than just ID
        // Use PATCH method with ID as a query parameter
        const responseData = await dispatch(
          patchDataThunk({
            url: `${apiUrls.generalProject}/?id=${patchData.ID}`,
            data: patchData,
          })
        ).unwrap();
        console.log('Job updated successfully:', responseData);
        
        // Update Redux store with the full job data
        const updatedJob = { ...selectedJob, ...patchData };
        dispatch(updateJob(updatedJob));
        
        // Close modal on success
        handleCloseModal();
      } else {
        // No changes made, just close the modal
        handleCloseModal();
      }
    } catch (error) {
      console.error('Error saving job:', error);
      // You could add error notification here if you have a notification system
      alert(`Failed to update job: ${error.message}`);
    }
  };
  
  // Render job card
  const renderJobCard = (job) => {
    const isOverdue = job.dueDate && moment(job.dueDate).isBefore(moment(), 'day');
    const priority = job.JobType?.toLowerCase().includes('high priority');
    
    // Calculate schedule week for past due items
    let scheduleWeekText = '';
    if (job.dueDate && week1StartDate && moment(job.dueDate).isBefore(week1StartDate, 'day')) {
      // Find which week the job was due in
      const dueMoment = moment(job.dueDate);
      let weekNumber = 0;
      
      // Search backwards from week 1 to find the week
      for (let i = 0; i < 52; i++) {
        const weekStart = moment(week1StartDate).subtract(i, 'weeks').startOf('week');
        const weekEnd = moment(weekStart).endOf('week');
        if (dueMoment.isBetween(weekStart, weekEnd, 'day', '[]')) {
          weekNumber = -i;
          break;
        }
      }
      
      if (weekNumber < 0) {
        scheduleWeekText = ` (Week ${weekNumber})`;
      }
    }
    
    return (
      <Card 
        key={`${job.ID}-${job.engineerType}`}
        className={`job-card ${job.isDone ? 'completed' : ''} ${isOverdue ? 'overdue' : ''} ${priority ? 'high-priority' : ''}`}
        onDoubleClick={() => handleJobDoubleClick(job)}
        style={{ cursor: hasEditAccess ? 'pointer' : 'default' }}
      >
        <Card.Body className="p-2">
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              <div className="d-flex align-items-baseline">
                <span className="job-number fw-bold me-2">{job.JobNumber}</span>
                <span className="project-name text-truncate" title={job.ProjectName}>
                  {job.ProjectName}
                </span>
              </div>
              <div className="model-number text-muted small">{job.ModelNumber}</div>
              {job.dueDate && (
                <div className="due-date small mt-1">
                  Due: {moment(job.dueDate).format('MM/DD')} - {job.duration} day{job.duration !== 1 ? 's' : ''}{scheduleWeekText}
                </div>
              )}
            </div>
            <div className="text-end ms-2">
              {job.isDone && <Badge bg="success" className="me-1">✓</Badge>}
              {priority && <Badge bg="warning">!</Badge>}
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };
  
  // Helper function to get holidays for a specific week
  const getHolidaysForWeek = (week) => {
    console.log('getHolidaysForWeek called with week:', week);
    console.log('Available holidays:', holidays);
    
    if (!holidays || !holidays.length) {
      console.log('No holidays available');
      return [];
    }
    
    const weekStart = moment(week.startDate).startOf('day');
    const weekEnd = moment(week.endDate).endOf('day');
    
    console.log('Week range:', {
      start: weekStart.format('YYYY-MM-DD'),
      end: weekEnd.format('YYYY-MM-DD')
    });
    
    const weekHolidays = holidays.filter(holiday => {
      try {
        // Holiday API structure: { ID, Holiday, HolidayDate }
        const dateField = holiday.HolidayDate;
        if (!dateField) return false;
        
        // Parse the UTC date and format it to local date string to avoid timezone issues
        const holidayDate = moment.utc(dateField).format('YYYY-MM-DD');
        const weekStartStr = weekStart.format('YYYY-MM-DD');
        const weekEndStr = weekEnd.format('YYYY-MM-DD');
        
        const isInRange = holidayDate >= weekStartStr && holidayDate <= weekEndStr;
        
        console.log('Checking holiday:', {
          name: holiday.Holiday,
          date: holidayDate,
          weekStart: weekStartStr,
          weekEnd: weekEndStr,
          isInRange
        });
        
        return isInRange;
      } catch (error) {
        console.error('Error parsing holiday date:', error, holiday);
        return false;
      }
    });
    
    console.log('Found holidays for week:', weekHolidays);
    return weekHolidays;
  };
  
  // Render PTO blocks for a specific week
  const renderPtoBlocksForWeek = (engineerName, week) => {
    const engineerPto = ptoEntries.filter(pto => pto.UserName === engineerName);
    const weekStart = moment(week.startDate).startOf('day');
    const weekEnd = moment(week.endDate).endOf('day');
    
    return engineerPto.map(pto => {
      // Parse dates using the unified formatter to handle UTC dates properly
      const startDateStr = formatDateUnified(pto.StartDate, 'iso', { preserveLocal: true });
      const endDateStr = formatDateUnified(pto.EndDate, 'iso', { preserveLocal: true });
      
      // Create moment objects from the properly formatted date strings
      const startDate = moment(startDateStr).startOf('day');
      const endDate = moment(endDateStr).startOf('day');
      
      
      if (startDate.isBetween(weekStart, weekEnd, 'day', '[]') || 
          endDate.isBetween(weekStart, weekEnd, 'day', '[]') ||
          (startDate.isBefore(weekStart) && endDate.isAfter(weekEnd))) {
        
       
        return (
          <Card 
            key={`pto-${pto.ID}-${week.weekNum}`}
            className="pto-card mb-1 bg-info bg-opacity-25 border-info"
            style={{ minHeight: '48px' }}
          >
            <Card.Body className="p-2 d-flex flex-column justify-content-center align-items-center">
              <div className="fw-bold text-info">PTO</div>
              <div className="small text-muted">
                {startDate.format('MM/DD')} - {endDate.format('MM/DD')}
              </div>
            </Card.Body>
          </Card>
        );
      }
      return null;
    }).filter(Boolean);
  };
  
  return (
    <div className="engineering-schedule-optimizer">
      {/* Filter Controls */}
      <Card className="mb-3">
        <Card.Header className="eso-compact-heading">
          <h5 className="mb-0">Engineering Schedule Optimizer</h5>
          <Button
            variant="outline-primary"
            className="eso-compact-filter-toggle"
            onClick={() => setShowCompactFilters(isOpen => !isOpen)}
            aria-expanded={showCompactFilters}
            aria-controls="eso-compact-filters"
          >
            <i className="bi bi-funnel me-2"></i>
            Filters
            <i className={`bi ${showCompactFilters ? 'bi-chevron-up' : 'bi-chevron-down'} ms-auto`}></i>
          </Button>
        </Card.Header>
        <Card.Body
          id="eso-compact-filters"
          className={`eso-compact-filter-panel ${showCompactFilters ? 'is-open' : ''}`}
        >
          <Row>
            <Col md={6}>
              {/* Engineer multi-select filters by discipline - Full width */}
              <Form.Group className="mb-3">
                <Form.Label>Engineers</Form.Label>
                <Row>
                  <Col md={4}>
                    <div className="d-flex align-items-center mb-1">
                      <Form.Check
                        type="checkbox"
                        id="mechanical-dept"
                        label="Mechanical"
                        checked={filters.departments.mechanical}
                        onChange={(e) => setFilters(prev => ({
                          ...prev,
                          departments: { ...prev.departments, mechanical: e.target.checked }
                        }))}
                        style={{ fontWeight: 500, fontSize: '0.9rem' }}
                      />
                    </div>
                    <Form.Control
                      as="select"
                      multiple
                      size={5}
                      value={filters.engineers.mechanical}
                      onChange={e => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setFilters(prev => ({ ...prev, engineers: { ...prev.engineers, mechanical: selected } }));
                      }}
                      style={{ width: '100%' }}
                      disabled={!filters.departments.mechanical}
                    >
                      {engineers.mechanical.map(eng => (
                        <option key={eng.name} value={eng.name}>{eng.name}</option>
                      ))}
                    </Form.Control>
                  </Col>
                  <Col md={4}>
                    <div className="d-flex align-items-center mb-1">
                      <Form.Check
                        type="checkbox"
                        id="electrical-dept"
                        label="Electrical"
                        checked={filters.departments.electrical}
                        onChange={(e) => setFilters(prev => ({
                          ...prev,
                          departments: { ...prev.departments, electrical: e.target.checked }
                        }))}
                        style={{ fontWeight: 500, fontSize: '0.9rem' }}
                      />
                    </div>
                    <Form.Control
                      as="select"
                      multiple
                      size={5}
                      value={filters.engineers.electrical}
                      onChange={e => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setFilters(prev => ({ ...prev, engineers: { ...prev.engineers, electrical: selected } }));
                      }}
                      style={{ width: '100%' }}
                      disabled={!filters.departments.electrical}
                    >
                      {engineers.electrical.map(eng => (
                        <option key={eng.name} value={eng.name}>{eng.name}</option>
                      ))}
                    </Form.Control>
                  </Col>
                  <Col md={4}>
                    <div className="d-flex align-items-center mb-1">
                      <Form.Check
                        type="checkbox"
                        id="controls-dept"
                        label="Controls"
                        checked={filters.departments.controls}
                        onChange={(e) => setFilters(prev => ({
                          ...prev,
                          departments: { ...prev.departments, controls: e.target.checked }
                        }))}
                        style={{ fontWeight: 500, fontSize: '0.9rem' }}
                      />
                    </div>
                    <Form.Control
                      as="select"
                      multiple
                      size={5}
                      value={filters.engineers.controls}
                      onChange={e => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setFilters(prev => ({ ...prev, engineers: { ...prev.engineers, controls: selected } }));
                      }}
                      style={{ width: '100%' }}
                      disabled={!filters.departments.controls}
                    >
                      {engineers.controls.map(eng => (
                        <option key={eng.name} value={eng.name}>{eng.name}</option>
                      ))}
                    </Form.Control>
                  </Col>
                </Row>
                <div className="small text-muted mt-2">Hold Ctrl (Windows) or Cmd (Mac) to select multiple engineers.</div>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Week</Form.Label>
                    <Form.Select
                      value={filters.startWeek}
                      onChange={(e) => setFilters(prev => ({ ...prev, startWeek: parseInt(e.target.value) }))}
                    >
                      {workWeekColumns.map((week, index) => (
                        <option key={index + 1} value={index + 1}>
                          {week.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>End Week</Form.Label>
                    <Form.Select
                      value={filters.endWeek}
                      onChange={(e) => setFilters(prev => ({ ...prev, endWeek: parseInt(e.target.value) }))}
                    >
                      {workWeekColumns.map((week, index) => (
                        <option key={index + 1} value={index + 1}>
                          {week.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Job Types</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="checkbox"
                    label="All"
                    checked={filters.jobTypes.all}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      jobTypes: { ...prev.jobTypes, all: e.target.checked }
                    }))}
                  />
                  {Object.entries(filters.jobTypes).filter(([key]) => key !== 'all').map(([type, checked]) => (
                    <Form.Check
                      key={type}
                      inline
                      type="checkbox"
                      label={type.toUpperCase()}
                      checked={checked}
                      disabled={filters.jobTypes.all}
                      onChange={(e) => setFilters(prev => ({
                        ...prev,
                        jobTypes: { ...prev.jobTypes, [type]: e.target.checked }
                      }))}
                    />
                  ))}
                </div>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <div>
                  <Form.Check
                    inline
                    type="checkbox"
                    label="Show Completed"
                    checked={filters.showCompleted}
                    onChange={(e) => setFilters(prev => ({ ...prev, showCompleted: e.target.checked }))}
                  />
                  <Form.Check
                    inline
                    type="checkbox"
                    label="Show Unscheduled"
                    checked={filters.showUnscheduled}
                    onChange={(e) => setFilters(prev => ({ ...prev, showUnscheduled: e.target.checked }))}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Mobile schedule places the engineer above a separately scrolling week grid. */}
      <div className="engineering-optimizer-mobile-schedule">
        {Object.entries(organizedJobs).map(([engineerName, data]) => {
          const columnCount = visibleWeeks.length + 1 + (filters.showUnscheduled ? 1 : 0);

          return (
            <section className="engineering-optimizer-mobile-engineer" key={`mobile-${engineerName}`}>
              <div className="engineering-optimizer-mobile-engineer-heading">
                <div className="engineer-name fw-bold">{engineerName}</div>
                <Badge bg={
                  data.engineer.type === 'mechanical' ? 'success' :
                  data.engineer.type === 'electrical' ? 'primary' : 'warning'
                }>
                  {data.engineer.type.charAt(0).toUpperCase() + data.engineer.type.slice(1)}
                </Badge>
              </div>

              <div className="engineering-optimizer-mobile-weeks-scroll">
                <div
                  className="engineering-optimizer-mobile-weeks-grid"
                  style={{ '--engineering-week-column-count': columnCount }}
                >
                  <div className="engineering-optimizer-mobile-week-heading is-past-due">Past Due</div>
                  {visibleWeeks.map((week) => (
                    <div className="engineering-optimizer-mobile-week-heading" key={`heading-${engineerName}-${week.weekNum}`}>
                      {week.label || `Week ${week.isoWeekNum}`}
                    </div>
                  ))}
                  {filters.showUnscheduled && (
                    <div className="engineering-optimizer-mobile-week-heading is-unscheduled">Unscheduled</div>
                  )}

                  <div className="engineering-optimizer-mobile-week-cell past-due-cell">
                    {data.pastDue.map(job => renderJobCard(job))}
                    {data.pastDue.length === 0 && <span className="empty-week">No jobs</span>}
                  </div>
                  {visibleWeeks.map((week) => {
                    const ptoBlocks = renderPtoBlocksForWeek(engineerName, week);
                    const weekJobs = data.weeks[week.weekNum] || [];

                    return (
                      <div className="engineering-optimizer-mobile-week-cell" key={`jobs-${engineerName}-${week.weekNum}`}>
                        {ptoBlocks}
                        {weekJobs.map(job => renderJobCard(job))}
                        {ptoBlocks.length === 0 && weekJobs.length === 0 && <span className="empty-week">No work</span>}
                      </div>
                    );
                  })}
                  {filters.showUnscheduled && (
                    <div className="engineering-optimizer-mobile-week-cell unscheduled-cell">
                      {data.unscheduled.map(job => renderJobCard(job))}
                      {data.unscheduled.length === 0 && <span className="empty-week">No jobs</span>}
                    </div>
                  )}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* Desktop and tablet schedule table. */}
      <div className="table-container engineering-optimizer-desktop-schedule" style={{ height: '70vh', overflow: 'auto' }}>
        <div className="engineering-schedule-optimizer">
          <Table bordered hover className="mb-5 engineering-optimizer-table">
            <thead>
            <tr>
              <th className="engineering-optimizer-engineer" style={{width: '200px'}}>Engineer</th>
              {/* Past Due column with fixed width */}
              <th className="text-center bg-danger text-white" style={{width: '120px', minWidth: '120px'}}>Past Due</th>
              {visibleWeeks.map(week => {
                const weekHolidays = getHolidaysForWeek(week);
                
                return (
                  <th key={week.weekNum} className="text-center bg-light" style={{minWidth: '150px'}}>
                    <div className="fw-bold">{week.label || `Week ${week.isoWeekNum}`}</div>
                  {weekHolidays.length > 0 && (
                      <div className="holiday-notice mt-2">
                        {weekHolidays.map((holiday, index) => (
                          <div key={holiday.ID} className="holiday-header-badge">
                            <Badge bg="warning" text="dark" className="w-100 py-1">
                              <div style={{ fontSize: '0.7rem', lineHeight: '1.1' }}>
                                <strong>{holiday.Holiday}</strong>
                                <br />
                                {moment.utc(holiday.HolidayDate).format('ddd MM/DD')}
                              </div>
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </th>
                );
              })}
              {filters.showUnscheduled && (
                <th className="text-center bg-warning" style={{width: '130px', minWidth: '130px'}}>Unscheduled</th>
              )}
            </tr>
          </thead>
          <tbody>
            {Object.entries(organizedJobs).map(([engineerName, data]) => (
              <tr key={engineerName}>
                <td className="engineering-optimizer-engineer">
                  <div className="engineer-name fw-bold">{engineerName}</div>
                  <div className="engineer-type text-muted small">
                    <Badge bg={
                      data.engineer.type === 'mechanical' ? 'success' : 
                      data.engineer.type === 'electrical' ? 'primary' : 'warning'
                    }>
                      {data.engineer.type.charAt(0).toUpperCase() + data.engineer.type.slice(1)}
                    </Badge>
                  </div>
                </td>
                {/* Past Due jobs */}
                <td className="week-cell past-due-cell bg-danger bg-opacity-10">
                  {data.pastDue.map(job => renderJobCard(job))}
                  {data.pastDue.length > 0 && (
                    <div className="text-danger small mt-2">
                      {data.pastDue.length} job{data.pastDue.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </td>
                {visibleWeeks.map(week => (
                  <td key={week.weekNum} className="week-cell">
                    {/* PTO blocks for this specific week */}
                    {renderPtoBlocksForWeek(engineerName, week)}
                    {/* Job cards for this specific week */}
                    {data.weeks[week.weekNum]?.map(job => renderJobCard(job))}
                  </td>
                ))}
                {filters.showUnscheduled && (
                  <td className="week-cell unscheduled-cell bg-light">
                    {data.unscheduled.map(job => renderJobCard(job))}
                    {data.unscheduled.length > 0 && (
                      <div className="text-muted small mt-2">
                        {data.unscheduled.length} job{data.unscheduled.length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      </div>
      
      {/* Job Edit Modal */}
      <Modal className="job-schedule-modal" show={showJobModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Job: {selectedJob?.JobNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingJob && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Project Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingJob.ProjectName || ''}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Model Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingJob.ModelNumber || ''}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Show only the relevant discipline's fields based on the selected job */}
              <Row>
                {selectedJob?.engineerType === 'mechanical' && (
                  <>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mechanical Engineer</Form.Label>
                        <Form.Select
                          value={editingJob.MechanicalEngineer || ''}
                          onChange={(e) => setEditingJob(prev => ({ ...prev, MechanicalEngineer: e.target.value }))}
                        >
                          <option value="">Select Engineer</option>
                          {engineers.mechanical.map(eng => (
                            <option key={eng.name} value={eng.name}>{eng.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mechanical Due Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={editingJob.MechanicalDue ? moment(editingJob.MechanicalDue).format('YYYY-MM-DD') : ''}
                          onChange={(e) => setEditingJob(prev => ({ ...prev, MechanicalDue: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mechanical Duration (days)</Form.Label>
                        <Form.Control
                          type="number"
                          value={editingJob.MechanicalDuration || ''}
                          onChange={(e) => setEditingJob(prev => ({ ...prev, MechanicalDuration: e.target.value }))}
                          min="1"
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
                
                {selectedJob?.engineerType === 'electrical' && (
                  <>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Electrical Engineer</Form.Label>
                        <Form.Select
                          value={editingJob.ElectricalEngineer || ''}
                          onChange={(e) => setEditingJob(prev => ({ ...prev, ElectricalEngineer: e.target.value }))}
                        >
                          <option value="">Select Engineer</option>
                          {engineers.electrical.map(eng => (
                            <option key={eng.name} value={eng.name}>{eng.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Electrical Due Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={editingJob.ElectricalDue ? moment(editingJob.ElectricalDue).format('YYYY-MM-DD') : ''}
                          onChange={(e) => setEditingJob(prev => ({ ...prev, ElectricalDue: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Electrical Duration (days)</Form.Label>
                        <Form.Control
                          type="number"
                          value={editingJob.ElectricalDuration || ''}
                          onChange={(e) => setEditingJob(prev => ({ ...prev, ElectricalDuration: e.target.value }))}
                          min="1"
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
                
                {selectedJob?.engineerType === 'controls' && (
                  <>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Controls Engineer</Form.Label>
                        <Form.Select
                          value={editingJob.ControlsEngineer || ''}
                          onChange={(e) => setEditingJob(prev => ({ ...prev, ControlsEngineer: e.target.value }))}
                        >
                          <option value="">Select Engineer</option>
                          {engineers.controls.map(eng => (
                            <option key={eng.name} value={eng.name}>{eng.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Controls Due Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={editingJob.ControlsDue ? moment(editingJob.ControlsDue).format('YYYY-MM-DD') : ''}
                          onChange={(e) => setEditingJob(prev => ({ ...prev, ControlsDue: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Controls Duration (days)</Form.Label>
                        <Form.Control
                          type="number"
                          value={editingJob.ControlsDuration || ''}
                          onChange={(e) => setEditingJob(prev => ({ ...prev, ControlsDuration: e.target.value }))}
                          min="1"
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveJob}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EngineeringScheduleOptimizer;
